import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import ExlyTable from "common/Components/ExlyTable";
import SelectionCard from "features/BulkActions/modules/SelectionCard/SelectionCard";
import {
  getSessionParticipantsColumnConfig,
  getTabFilterConfig,
  filterConfig,
  tableChipsFormatter,
} from "./sessionParticipantsTableConfig";
import { empty_state_props } from "../../ZoomAttendanceOfListing.helper";
import { ZOOM_ATTENDANCE_CONSTANTS } from "../../constants/ZoomAttendance.constants";
import { useSessionParticipantsStore } from "../../store/useSessionParticipantsStore";
import { useSessionParticipantsContext } from "../SessionParticipantsPage/context/SessionParticipantsContext";

import { app_route_ids, app_route_keys } from "constants/urlPaths";

export const SessionParticipantsTable = ({
  tableTitle: propTableTitle,
  customBackAction: propCustomBackAction,
  noExportButton = false,
  selected: propSelected,
  onRowSelected: propOnRowSelected,
  onReconcileClick: propOnReconcileClick,
  onViewReportClick: propOnViewReportClick,
  onMapToBooking,
  onCreateNewBooking: propOnCreateNewBooking,
  customHandleExport: propCustomHandleExport,
  sessionDetails: propSessionDetails,
  filterValues: propFilterValues,
  bulkActionComponent: propBulkActionComponent,
  allValuesSelected: propAllValuesSelected,
  footerProps: propFooterProps,
  ...props
}) => {
  const context = useSessionParticipantsContext();

  const selected = propSelected ?? context.selectedIds ?? [];
  const onRowSelected = propOnRowSelected ?? context.onRowSelectedHandler;
  const onReconcileClick = propOnReconcileClick ?? context.handleOpenReconcile;
  const onViewReportClick = propOnViewReportClick ?? context.handleOpenReport;
  const onCreateNewBooking =
    propOnCreateNewBooking ?? context.handleOpenCreateNewBooking;
  const customHandleExport = propCustomHandleExport ?? context.handleExport;
  const customBackAction = propCustomBackAction ?? context.handleBack;
  const sessionDetails = propSessionDetails ?? context.sessionDetails;
  const filterValues = propFilterValues ?? context.filterValues ?? {};
  const allValuesSelected = propAllValuesSelected ?? context.selectAll;
  const tableTitle = propTableTitle;
  const footerProps = propFooterProps ?? context.footerProps;

  const defaultBulkActionComponent = context.bulkActions ? (
    <SelectionCard
      actions={context.bulkActions}
      selectedRecords={Object.keys(context.selectedRecords || {})}
      totalRecords={context.total || 0}
      currentPageFullSelection={context.currentPageFullSelection}
      entityName={ZOOM_ATTENDANCE_CONSTANTS.LABELS.ATTENDEE_ENTITY}
      selectAll={context.selectAll}
      handleSelectAll={context.handleSelectAll}
    />
  ) : null;

  const bulkActionComponent =
    propBulkActionComponent ?? defaultBulkActionComponent;

  const location = useLocation();
  const storeIsUnrecognizedView = useSessionParticipantsStore(
    (state) => state.isUnrecognizedView
  );
  const setStoreFilterValues = useSessionParticipantsStore(
    (state) => state.setFilterValues
  );

  const tabFilterConfig = getTabFilterConfig({
    unrecognizedCount:
      sessionDetails?.unrecognized || ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO,
  });

  const urlParams = new URLSearchParams(location.search);
  const filterParam = urlParams.get("filter");
  let parsedFilter = {};
  try {
    if (filterParam) parsedFilter = JSON.parse(filterParam);
  } catch (e) {}

  useEffect(() => {
    const currentFilters = useSessionParticipantsStore.getState().filterValues;
    if (JSON.stringify(currentFilters) !== JSON.stringify(parsedFilter)) {
      setStoreFilterValues(parsedFilter);
    }
  }, [location.search, setStoreFilterValues]);

  const currentAttendanceType =
    filterValues?.attendance_type ??
    props?.filterValues?.attendance_type ??
    parsedFilter?.attendance_type;

  const isUnrecognizedView =
    storeIsUnrecognizedView ||
    Number(currentAttendanceType) ===
      ZOOM_ATTENDANCE_CONSTANTS.PARTICIPANT_ATTENDANCE_TYPES.UNRECOGNIZED ||
    currentAttendanceType ===
      ZOOM_ATTENDANCE_CONSTANTS.PARTICIPANT_ATTENDANCE_TYPES.UNRECOGNIZED ||
    currentAttendanceType === ZOOM_ATTENDANCE_CONSTANTS.TAB_VALUES.UNRECOGNIZED;

  const columnConfig = getSessionParticipantsColumnConfig({
    onReconcileClick,
    onViewReportClick,
    onMapToBooking,
    onCreateNewBooking,
    isUnrecognizedView,
  });

  return (
    <div className="tw-w-full tw-px-0 sm:tw-px-5 tw-pt-3 tw-overflow-x-auto tw-max-w-full">
      <ExlyTable
        ra_props={{
          resource:
            app_route_ids[app_route_keys.zoom_attendance_session_participants],
          basePath:
            app_route_ids[app_route_keys.zoom_attendance_session_participants],
          hasCreate: false,
          hasEdit: false,
          hasList: true,
          hasShow: false,
          ...props,
        }}
        bulkActionComponent={isUnrecognizedView ? null : bulkActionComponent}
        allValuesSelected={allValuesSelected}
        showTopBars={true}
        checkboxSelection={Boolean(true)}
        showSelectAllcheckBox={Boolean(true)}
        selected={selected}
        onRowSelected={onRowSelected}
        noExportButton={noExportButton}
        tableTitle={tableTitle}
        customBackAction={customBackAction}
        customHandleExport={customHandleExport}
        tabFilterConfig={tabFilterConfig}
        layoutProps={{
          paddingDesktop: ZOOM_ATTENDANCE_CONSTANTS.LAYOUT.PADDING_DESKTOP_ZERO,
          paddingBottom: ZOOM_ATTENDANCE_CONSTANTS.LAYOUT.PADDING_BOTTOM_60PX,
          noMobileBoxShadow: Boolean(true),
        }}
        columnConfig={columnConfig}
        tableFilters={filterConfig}
        tableChipsFormatter={tableChipsFormatter}
        borderedFields
        fieldsLeftPadded
        drawerFieldsBordered
        fieldsAlignment={
          ZOOM_ATTENDANCE_CONSTANTS.FIELDS_ALIGNMENT_SPACE_BETWEEN
        }
        drawerFieldsAlignment={
          ZOOM_ATTENDANCE_CONSTANTS.FIELDS_ALIGNMENT_SPACE_BETWEEN
        }
        showFixedBars
        footerProps={footerProps}
        primaryKey={ZOOM_ATTENDANCE_CONSTANTS.LAYOUT.PRIMARY_KEY_ID}
        uniqueRowIdFieldName={ZOOM_ATTENDANCE_CONSTANTS.LAYOUT.PRIMARY_KEY_ID}
        {...empty_state_props}
      />
      <div className="tw-mt-2 tw-mb-4 tw-px-2 tw-text-xs tw-text-gray-500 tw-italic">
        {ZOOM_ATTENDANCE_CONSTANTS.LABELS.ZOOM_EMAIL_NOTICE}
      </div>
    </div>
  );
};
